import React, { useState, useRef, useEffect } from "react";
import Modal from "../../components/Modal";
import InputField from "../../components/InputField";
import DropDown from "../../components/DropDown";
import Pagination from "../../components/Pagination";
import { useParentCodeData } from "./ParentCodeData";
import { entityActiveInactiveAction } from "../../actions/entityActiveInactiveAction";
import { toast } from "react-toastify";
import "./index.scss";

const ITEMS_PER_PAGE = 8;
const SIDEBAR_PAGE_SIZE = 10;

const Field = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  placeholder,
  readOnly,
}) => (
  <InputField
    label={label}
    name={name}
    type="text"
    value={value}
    onChange={onChange}
    error={error}
    required={required}
    placeholder={placeholder}
    readOnly={readOnly}
  />
);

const EditIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const MoreIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="19" r="1" />
  </svg>
);

// ── Dropdown action menu ───────────────────────────────────────────────────────
const ActionMenu = ({ onEdit, onView }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="pc-action-menu" ref={ref}>
      <button
        className="pc-icon-btn pc-icon-btn--more"
        onClick={() => setOpen((v) => !v)}
      >
        <MoreIcon />
      </button>
      {open && (
        <div className="pc-action-menu__dropdown">
          <button
            className="pc-action-menu__item"
            onClick={() => {
              onView();
              setOpen(false);
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View
          </button>
          <button
            className="pc-action-menu__item"
            onClick={() => {
              onEdit();
              setOpen(false);
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const ParentCode = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewingChild, setViewingChild] = useState(null);
  const [sidebarPage, setSidebarPage] = useState(1);

  // ── Status modal state ─────────────────────────────────────────────────────
  const [statusModal, setStatusModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null); // the child card
  const [statusValue, setStatusValue] = useState(true); // toggle: true=active
  const [statusLoading, setStatusLoading] = useState(false);

  const openStatusModal = (child) => {
    setStatusTarget(child);
    setStatusValue(true); // default to active each time
    setStatusModal(true);
  };

  const closeStatusModal = () => {
    setStatusModal(false);
    setStatusTarget(null);
  };

  const handleStatusSubmit = async () => {
    if (!statusTarget) return;
    const id = Number(statusTarget.childCode); // childCode = String(record.id)
    setStatusLoading(true);
    const result = await entityActiveInactiveAction(id, statusValue);
    setStatusLoading(false);
    if (result.success) {
      toast.success(result.message || "Status updated!");
      closeStatusModal();
    } else {
      toast.error(result.message || "Failed to update status");
    }
  };

  const {
    parents,
    children,
    allChildren,
    isLoading,
    selectedParent,
    setSelectedParent,
    parentModal,
    editingParent,
    openParentModal,
    openEditParentModal,
    closeParentModal,
    parentForm,
    parentErrors,
    handleParentChange,
    submitParent,
    childModal,
    editingChild,
    openChildModal,
    openEditChildModal,
    closeChildModal,
    childForm,
    childErrors,
    handleChildChange,
    submitChild,
  } = useParentCodeData();

  const handleSelectParent = (code) => {
    setSelectedParent(code);
    setCurrentPage(1);
    setSearchQuery("");
  };

  // Sidebar pagination
  const sidebarTotalPages = Math.ceil(parents.length / SIDEBAR_PAGE_SIZE);
  const pagedParents = parents.slice(
    (sidebarPage - 1) * SIDEBAR_PAGE_SIZE,
    sidebarPage * SIDEBAR_PAGE_SIZE,
  );

  const filteredChildren = children.filter(
    (c) =>
      c.childName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.childCode.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalItems = filteredChildren.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const pagedChildren = filteredChildren.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const companyOptions = [
    { value: "", label: "Select company" },
    ...parents.map((p) => ({ value: p.parentCode, label: p.parentName })),
  ];

  return (
    <div className="pc-page">
      {/* Header */}
      <div className="pc-header">
        <div>
          <h1 className="pc-header__title">Company &amp; Distributor</h1>
          <p className="pc-header__sub">Manage companies and distributors</p>
        </div>
      </div>

      {isLoading ? (
        <div className="pc-loading">Loading...</div>
      ) : (
        <div className="pc-layout">
          <div className="pc-sidebar">
            <p className="pc-sidebar__label">COMPANIES</p>
            <div
              className={`pc-sidebar__item ${!selectedParent ? "active" : ""}`}
              onClick={() => handleSelectParent(null)}
            >
              <span>All</span>
              <span className="pc-sidebar__count">{allChildren.length}</span>
            </div>
            {pagedParents.map((p) => (
              <div
                key={p.parentCode}
                className={`pc-sidebar__item ${selectedParent === p.parentCode ? "active" : ""}`}
                onClick={() => handleSelectParent(p.parentCode)}
              >
                <span>{p.parentName}</span>
                <div className="pc-sidebar__item-right">
                  <span className="pc-sidebar__count">
                    {
                      allChildren.filter((c) => c.parentCode === p.parentCode)
                        .length
                    }
                  </span>
                </div>
              </div>
            ))}

            {/* Sidebar pagination */}
            {sidebarTotalPages > 1 && (
              <div className="pc-sidebar__pagination">
                <button
                  className="pc-sidebar__page-btn"
                  disabled={sidebarPage === 1}
                  onClick={() => setSidebarPage((p) => p - 1)}
                >
                  ‹
                </button>
                <span className="pc-sidebar__page-info">
                  {sidebarPage} / {sidebarTotalPages}
                </span>
                <button
                  className="pc-sidebar__page-btn"
                  disabled={sidebarPage === sidebarTotalPages}
                  onClick={() => setSidebarPage((p) => p + 1)}
                >
                  ›
                </button>
              </div>
            )}
          </div>

          {/* // "${selectedParent}" */}
          {/* Main */}
          <div className="pc-main">
            <div className="pc-main__topbar">
              <h2 className="pc-main__title">
                {selectedParent
                  ? parents.find((p) => p.parentCode === selectedParent)
                      ?.parentName || "No Parents"
                  : "All Distributors"}
              </h2>
              {/* <div className="pc-main__topbar-right">
                <div className="pc-search">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    className="pc-search__input"
                    placeholder="Search by name,code ..."
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  {searchQuery && (
                    <button
                      className="pc-search__clear"
                      onClick={() => {
                        setSearchQuery("");
                        setCurrentPage(1);
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div> */}
            </div>

            {totalItems === 0 ? (
              <div className="pc-empty">No distributors found</div>
            ) : (
              <>
                <div className="pc-cards">
                  {pagedChildren.map((c) => (
                    <div key={c.childCode} className="pc-card">
                      <div className="pc-card__body">
                        <div>
                          <h3 className="pc-card__name">{c.childName}</h3>
                          {/* <p className="pc-card__code">{c.childCode}</p> */}
                          <span className="pc-card__company">
                            {c.parentName || "No Parents"}
                          </span>
                        </div>
                        <div className="pc-card__right">
                          <button
                            className="pc-btn pc-btn--status"
                            onClick={() => openStatusModal(c)}
                          >
                            Change Status
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pc-pagination">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Company Modal */}
      <Modal
        isOpen={parentModal}
        onClose={closeParentModal}
        title={editingParent ? "Edit Company" : "Create Company"}
        size="medium"
      >
        <div className="pc-modal-form">
          <Field
            label="Company Code"
            name="parentCode"
            value={parentForm.parentCode}
            onChange={handleParentChange}
            error={parentErrors.parentCode}
            required
            placeholder="e.g. ABC0001"
            readOnly={!!editingParent}
          />
          <Field
            label="Company Name"
            name="parentName"
            value={parentForm.parentName}
            onChange={handleParentChange}
            error={parentErrors.parentName}
            required
            placeholder="Enter company name"
          />
          <Field
            label="Description"
            name="description"
            value={parentForm.description}
            onChange={handleParentChange}
            placeholder="Optional description"
          />
          {/* <DropDown
            label="Status"
            name="status"
            value={parentForm.status}
            onChange={handleParentChange}
            options={statusOptions}
            fullWidth
          /> */}
          <div className="pc-modal-actions">
            <button className="btn-cancel" onClick={closeParentModal}>
              Cancel
            </button>
            <button className="btn-submit" onClick={submitParent}>
              {editingParent ? "Save Changes" : "Create"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Distributor Modal */}
      <Modal
        isOpen={childModal}
        onClose={closeChildModal}
        title={editingChild ? "Edit Distributor" : "Create Distributor"}
        size="medium"
      >
        <div className="pc-modal-form">
          <DropDown
            label="Company"
            name="parentCode"
            value={childForm.parentCode}
            onChange={handleChildChange}
            options={companyOptions}
            error={childErrors.parentCode}
            required
            fullWidth
          />
          <Field
            label="Distributor Code"
            name="childCode"
            value={childForm.childCode}
            onChange={handleChildChange}
            error={childErrors.childCode}
            required
            placeholder="e.g. ABC0001-D01"
            readOnly={!!editingChild}
          />
          <Field
            label="Distributor Name"
            name="childName"
            value={childForm.childName}
            onChange={handleChildChange}
            error={childErrors.childName}
            required
            placeholder="Enter distributor name"
          />
          <Field
            label="Description"
            name="description"
            value={childForm.description}
            onChange={handleChildChange}
            placeholder="Optional description"
          />
          <DropDown
            label="Status"
            name="status"
            value={childForm.status}
            onChange={handleChildChange}
            options={statusOptions}
            fullWidth
          />
          <div className="pc-modal-actions">
            <button className="btn-cancel" onClick={closeChildModal}>
              Cancel
            </button>
            <button className="btn-submit" onClick={submitChild}>
              {editingChild ? "Save Changes" : "Create"}
            </button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      {viewingChild && (
        <Modal
          isOpen={!!viewingChild}
          onClose={() => setViewingChild(null)}
          title="Distributor Details"
          size="medium"
        >
          <div className="pc-view-modal">
            <div className="pc-view-row">
              <span>Distributor Code</span>
              <strong>{viewingChild.childCode}</strong>
            </div>
            <div className="pc-view-row">
              <span>Distributor Name</span>
              <strong>{viewingChild.childName}</strong>
            </div>
            <div className="pc-view-row">
              <span>Company</span>
              <strong>{viewingChild.parentCode}</strong>
            </div>
            <div className="pc-view-row">
              <span>Description</span>
              <strong>{viewingChild.description || "—"}</strong>
            </div>
            <div className="pc-view-row">
              <span>Status</span>
              <span className={`pc-status pc-status--${viewingChild.status}`}>
                {viewingChild.status}
              </span>
            </div>
          </div>
        </Modal>
      )}

      {/* Change Status Modal */}
      <Modal
        isOpen={statusModal}
        onClose={closeStatusModal}
        title="Change Status"
        size="small"
      >
        <div className="pc-status-modal">
          {/* Entity info strip */}
          <div className="pc-status-modal__info">
            <div className="pc-status-modal__avatar">
              {(statusTarget?.childName || "?")[0].toUpperCase()}
            </div>
            <div className="pc-status-modal__info-text">
              <p className="pc-status-modal__entity-name">
                {statusTarget?.childName || "—"}
              </p>
              <p className="pc-status-modal__entity-id">
                {statusTarget?.entityId || statusTarget?.childCode || ""}
              </p>
            </div>
          </div>

          <div className="pc-status-modal__divider" />

          {/* Toggle section */}
          <div className="pc-status-modal__section">
            <p className="pc-status-modal__section-label">Active Status</p>
            <p className="pc-status-modal__section-hint">
              Toggle to set whether this entity is active or inactive.
            </p>

            <div className="pc-status-modal__toggle-wrap">
              <button
                className={`pc-status-option ${!statusValue ? "pc-status-option--inactive" : ""}`}
                onClick={() => setStatusValue(false)}
              >
                <span className="pc-status-option__dot" />
                Inactive
              </button>

              <button
                className={`pc-toggle ${statusValue ? "pc-toggle--on" : "pc-toggle--off"}`}
                onClick={() => setStatusValue((v) => !v)}
                aria-label="Toggle active status"
              >
                <span className="pc-toggle__thumb" />
              </button>

              <button
                className={`pc-status-option ${statusValue ? "pc-status-option--active" : ""}`}
                onClick={() => setStatusValue(true)}
              >
                <span className="pc-status-option__dot" />
                Active
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="pc-status-modal__actions">
            <button
              className="pc-status-modal__btn-cancel"
              onClick={closeStatusModal}
            >
              Cancel
            </button>
            <button
              className="pc-status-modal__btn-confirm"
              onClick={handleStatusSubmit}
              disabled={statusLoading}
            >
              {statusLoading && <span className="pc-status-modal__spinner" />}
              {statusLoading ? "Saving…" : "Confirm"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ParentCode;
